import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Video } from 'lucide-react';
import type { PortalModelWalkthrough } from '../../types';
import {
  retryPortalWalkthroughProcessing,
  subscribePortalModelWalkthroughs,
} from '../../utils/portalWalkthroughService';

export interface PortalModelWalkthroughStaffPanelProps {
  portalId: string;
  dealId: string;
  highlightWalkthroughId?: string | null;
}

const PortalModelWalkthroughStaffPanel: React.FC<PortalModelWalkthroughStaffPanelProps> = ({
  portalId,
  dealId,
  highlightWalkthroughId,
}) => {
  const [rows, setRows] = useState<PortalModelWalkthrough[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    if (!portalId || !dealId) return undefined;
    return subscribePortalModelWalkthroughs(portalId, dealId, setRows);
  }, [portalId, dealId]);

  if (!rows.length) return null;

  const handleRetry = async (walkthroughId: string) => {
    setRetryingId(walkthroughId);
    try {
      await retryPortalWalkthroughProcessing(portalId, walkthroughId);
    } catch (err) {
      console.error('[walkthrough retry]', err);
      alert('Retry failed. Check Cloud Functions logs.');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="cw-stack" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Video size={14} />
        <span className="cw-form-label" style={{ margin: 0 }}>
          Client walkthroughs <span className="pmwr-beta" style={{ marginLeft: 6 }}>Beta</span>
        </span>
      </div>
      {rows.map(w => {
        const highlighted = highlightWalkthroughId === w.id;
        return (
          <div
            key={w.id}
            id={w.id ? `walkthrough-${w.id}` : undefined}
            style={{
              border: `1px solid ${highlighted ? '#a78bfa' : '#e8e5e0'}`,
              borderRadius: 10,
              padding: 12,
              background: highlighted ? '#faf5ff' : '#fff',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <div>
                <strong style={{ fontSize: 13 }}>{w.authorName}</strong>
                <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                  {w.createdAt.toLocaleString()} · {Math.round(w.durationSeconds)}s
                </span>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color:
                    w.status === 'ready'
                      ? '#166534'
                      : w.status === 'failed'
                        ? '#b91c1c'
                        : '#92400e',
                }}
              >
                {w.status}
              </span>
            </div>

            {w.downloadUrl && (
              <video
                src={w.downloadUrl}
                controls
                playsInline
                style={{ width: '100%', maxHeight: 280, borderRadius: 8, background: '#000', marginBottom: 10 }}
              />
            )}

            {w.status === 'ready' && w.summary && (
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                <p style={{ margin: '0 0 8px' }}>{w.summary}</p>
                {!!w.keyQuestions?.length && (
                  <div style={{ marginBottom: 6 }}>
                    <strong>Questions</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {w.keyQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {!!w.keyConcerns?.length && (
                  <div>
                    <strong>Concerns</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {w.keyConcerns.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {w.status === 'failed' && (
              <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 8 }}>
                {w.error || 'Processing failed.'}
                <button
                  type="button"
                  className="cw-mini-btn"
                  style={{ marginLeft: 8 }}
                  disabled={retryingId === w.id}
                  onClick={() => void handleRetry(w.id!)}
                >
                  {retryingId === w.id ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                  Retry
                </button>
              </div>
            )}

            {(w.status === 'processing' || w.status === 'uploading') && (
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                <Loader2 size={12} className="spin" style={{ marginRight: 6 }} />
                Processing walkthrough…
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PortalModelWalkthroughStaffPanel;
